import React, { useEffect, useState } from 'react';
import {
  Grid,
  Box,
  Button,
  Fade
} from '@material-ui/core';
import { TextField } from '@material-ui/core';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { toast } from "react-toastify";
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete } from "@material-ui/lab";
import BonClientService from '../services/BonClientService';

// Icônes Material-UI
import AddIcon from '@material-ui/icons/Add';
import PaymentIcon from '@material-ui/icons/Payment';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import ReceiptIcon from '@material-ui/icons/Receipt';

const useStyles = makeStyles((theme) => ({
  formContainer: {
    marginTop: theme.spacing(1),
  },
  gridItem: {
    padding: theme.spacing(1),
  },
  iconAdornment: {
    color: '#226D68',
    marginRight: theme.spacing(1),
  },
  submitButton: {
    background: 'linear-gradient(135deg, #226D68 0%, #2a8c82 100%)',
    color: 'white',
    fontWeight: 600,
    fontSize: '1rem',
    padding: '12px 40px',
    borderRadius: 8,
    boxShadow: '0 4px 15px rgba(34, 109, 104, 0.3)',
    textTransform: 'none',
    width: '100%',
    marginTop: theme.spacing(2),
    '&:hover': {
      background: 'linear-gradient(135deg, #1a5a55 0%, #226D68 100%)',
      boxShadow: '0 6px 20px rgba(34, 109, 104, 0.4)',
    },
    '&:disabled': {
      background: '#cbd5e1',
      boxShadow: 'none',
    },
  },
  formHeader: {
    textAlign: 'center',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
  },
  formTitle: {
    fontWeight: 700,
    color: '#226D68',
    marginBottom: theme.spacing(1),
    fontSize: '1.5rem',
  },
  formSubtitle: {
    color: '#64748b',
    fontSize: '0.9rem',
  },
  sectionTitle: {
    fontWeight: 600,
    color: '#226D68',
    marginBottom: theme.spacing(2),
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  infoSection: {
    backgroundColor: '#f0f9ff',
    padding: theme.spacing(2),
    borderRadius: 12,
    border: '2px solid #e0f2fe',
    marginBottom: theme.spacing(2),
    boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
  },
}));

const PayementForm = ({ bons, onClose, refresh }) => {
  const classes = useStyles();
  const auth = useSelector((state) => state.auth);

  const [typePayements, setTypePayements] = useState([]);
  const [idPayement, setIdPayement] = useState();
  const [produits, setProduits] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await BonClientService.getTypePayement();
        setTypePayements(response.data || []);
      } catch (error) {
        console.error('Erreur chargement types de paiement:', error);
        toast.error("Erreur lors du chargement des types de paiement");
      }
    };

    fetchData();
  }, []);

  const initialValues = {
    idType: '',
    numero: '',
  };

  const validationSchema = Yup.object().shape({
    idType: Yup.number().required("Type de paiement requis"),
    numero: Yup.string().when('idType', {
      is: (idType) => idType === 2 || idType === 3,
      then: Yup.string().required("Numéro requis pour ce type de paiement"),
      otherwise: Yup.string()
    }),
  });

  const onSubmit = async (values, props) => {
    try {
      const paiement = {
        "idType": values.idType,
        "numero": values.numero,
        "speudo": auth.nom,
      };

      // Préparer les produits avec les informations de paiement
      const produitsAvecPaiement = bons.map(product => ({
        ...product,
        numero: paiement.numero,
        idPaye: paiement.idType
      }));

      await BonClientService.payerTout({
        "prdVendus": produitsAvecPaiement,
        "paye": paiement.idType
      });
      
      toast.success("Tous les bons ont été payés avec succès", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      
      props.resetForm();
      setIdPayement('');
      
      // Fermer le dialog et rafraîchir si nécessaire
      if (onClose) onClose();
      if (refresh) refresh();
      
    } catch (error) {
      console.error('Erreur paiement des bons:', error);
      toast.error("Erreur lors du paiement des bons", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };

  return (
    <Box>
      {/* En-tête du formulaire */}
      <Box className={classes.formHeader}>
        <PaymentIcon style={{ fontSize: 40, color: '#226D68', marginBottom: 8 }} />
        <div className={classes.formTitle}>Paiement des Bons</div>
        <div className={classes.formSubtitle}>
          Payer tous les bons sélectionnés ({bons?.length || 0} bon(s))
        </div>
      </Box>

      {/* Section information */}
      <Box className={classes.infoSection}>
        <div className={classes.sectionTitle}>
          <ReceiptIcon /> Informations de paiement
        </div>
        <div style={{ color: '#64748b', fontSize: '0.875rem' }}>
          Vous allez régler <strong>{bons?.length || 0} bon(s)</strong> en une seule transaction
        </div>
      </Box>

      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
        {(props) => (
          <Form noValidate className={classes.formContainer}>
            <Grid container spacing={2}>
              
              {/* Type de paiement */}
              <Grid item xs={12}>
                <div className={classes.sectionTitle}>
                  <CreditCardIcon /> Mode de paiement
                </div>
              </Grid>

              <Grid item xs={12} className={classes.gridItem}>
                <Autocomplete
                  id="idType"
                  name="idType"
                  options={typePayements}
                  getOptionLabel={option => option.designation}
                  onChange={(e, v) => {
                    const idType = v?.id_type;
                    setIdPayement(idType);
                    props.setFieldValue("idType", idType);
                    // Réinitialiser le numéro quand le type change
                    if (idType !== 2 && idType !== 3) {
                      props.setFieldValue("numero", '');
                    }
                  }}
                  renderInput={params => (
                    <Field 
                      as={TextField} 
                      {...params} 
                      name="idType" 
                      label="Type de paiement" 
                      variant="outlined" 
                      fullWidth
                      size="small"
                      error={props.errors.idType && props.touched.idType}
                      helperText={<ErrorMessage name='idType' />}
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <CreditCardIcon className={classes.iconAdornment} />
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Numéro conditionnel */}
              {(idPayement === 2 || idPayement === 3) && (
                <Grid item xs={12}>
                  <Fade in timeout={500}>
                    <Box className={classes.gridItem}>
                      <Field 
                        as={TextField} 
                        name="numero" 
                        label={
                          idPayement === 2 ? 'Numéro de carte' : 
                          idPayement === 3 ? 'Numéro de chèque' : 'Numéro'
                        } 
                        fullWidth 
                        variant="outlined"
                        size="small"
                        error={props.errors.numero && props.touched.numero}
                        helperText={<ErrorMessage name='numero' />}
                        required
                        InputProps={{
                          startAdornment: <PaymentIcon className={classes.iconAdornment} />
                        }}
                        placeholder={
                          idPayement === 2 ? '1234 5678 9012 3456' : 
                          idPayement === 3 ? 'Numéro du chèque' : ''
                        }
                      />
                    </Box>
                  </Fade>
                </Grid>
              )}

              {/* Information sur le type sélectionné */}
              {idPayement && (
                <Grid item xs={12}>
                  <Fade in timeout={700}>
                    <Box style={{ 
                      padding: '12px 16px', 
                      backgroundColor: '#f0fdf4', 
                      border: '1px solid #bbf7d0',
                      borderRadius: '8px',
                      color: '#166534',
                      fontSize: '0.875rem',
                      marginBottom: '8px'
                    }}>
                      <strong>Type sélectionné :</strong> {
                        typePayements.find(t => t.id_type === idPayement)?.designation
                      }
                      {(idPayement === 2 || idPayement === 3) && ' - Numéro requis'}
                    </Box>
                  </Fade>
                </Grid>
              )}

              {/* Bouton de soumission */}
              <Grid item xs={12} className={classes.gridItem}>
                <Button 
                  type='submit' 
                  variant='contained'
                  className={classes.submitButton}
                  startIcon={<PaymentIcon />}
                  disabled={props.isSubmitting || !bons?.length}
                >
                  {props.isSubmitting ? 'Paiement en cours...' : `Payer ${bons?.length || 0} bon(s)`}
                </Button>
                {!bons?.length && (
                  <div style={{ 
                    color: '#ef4444', 
                    fontSize: '0.875rem', 
                    textAlign: 'center', 
                    marginTop: '8px' 
                  }}>
                    Aucun bon sélectionné pour le paiement
                  </div>
                )}
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
}

export default PayementForm;